import { z } from "zod";

/**
 * Central registry of feature flags.
 *
 * To add a new flag:
 *   1. Add an entry to {@link FEATURE_FLAGS} below with a `type` and `defaultValue`.
 *   2. The flag key automatically becomes part of the {@link FeatureFlagKey} union,
 *      and `getFlag(key)` calls become type-checked at compile time.
 *
 * The flag value type is inferred from the `type` field:
 *   - "boolean" -> boolean (PostHog Boolean flag)
 *   - "string"  -> string  (PostHog Multivariate flag — variant key)
 *
 * Defaults are returned by the FeatureFlagService when PostHog is unavailable
 * or the flag is unknown, so they must always be a safe fallback value.
 */

/**
 * The PostHog group type used to scope organization-level feature flags
 * and group analytics. PostHog requires a group "type" + "key" pair
 * (e.g. `{ company: "org_abc" }`).
 */
export const ORG_GROUP_TYPE = "company";

/** Supported value types for a feature flag. */
export type FeatureFlagValueType = "boolean" | "string";

/** Definition of a single feature flag entry in the registry. */
export interface FeatureFlagDefinition<
  T extends FeatureFlagValueType = FeatureFlagValueType,
> {
  type: T;
  defaultValue: FeatureFlagValueByType<T>;
  description?: string;
}

/** Maps a {@link FeatureFlagValueType} to its TypeScript value type. */
export type FeatureFlagValueByType<T extends FeatureFlagValueType> =
  T extends "boolean" ? boolean : T extends "string" ? string : never;

/**
 * The single source of truth for all feature flags in the application.
 * The key used here must match the flag key configured in PostHog.
 */
export const FEATURE_FLAGS = {
  "quote-review-editable-handoff": {
    type: "boolean",
    defaultValue: true,
    description:
      "Rollback switch for editable quote-review handoffs and SOP-defined preliminary review guidance.",
  },
  "xlsx-validation-agent-feedback": {
    type: "boolean",
    defaultValue: false,
    description:
      "Enable XLSX repair-prevention skill guidance and return advisory findings to the agent. Validation and warning logs remain active when disabled.",
  },
  "hubspot-erp-integration": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the HubSpot ERP integration is exposed to users (e.g. shown in the ERP provider dropdown).",
  },
  USE_GPT_SOL_6_1: {
    type: "boolean",
    defaultValue: false,
    description:
      "Use GPT-6.1 Sol medium for the standard agent, taking precedence over USE_GPT and Claude model flags.",
  },
  USE_GPT_LUNA_6: {
    type: "boolean",
    defaultValue: false,
    description:
      "Use GPT-6 Luna in the agent builders that support it. Today that is the standard agent's item search sub-agent, at xhigh reasoning, instead of Claude Sonnet 4.6.",
  },
  USE_GPT: {
    type: "boolean",
    defaultValue: false,
    description:
      "Use GPT-5.6 Sol medium instead of the Claude Opus default for the standard agent.",
  },
  USE_CLAUDE_OPUS_4_8: {
    type: "boolean",
    defaultValue: false,
    description:
      "Use Claude Opus 4.8 instead of Claude Opus 4.6 for the standard agent when USE_GPT is disabled.",
  },
  USE_CLAUDE_OPUS_FAST_MODE: {
    type: "boolean",
    defaultValue: false,
    description:
      "Enable fast mode for Claude Opus 4.6, Claude Opus 4.8, GPT-5.6 Sol, GPT-6.1 Sol, and GPT-6 Luna requests.",
  },
  "llm-provider-failover": {
    type: "boolean",
    defaultValue: true,
    description:
      "Kill switch for automatic cross-provider LLM failover (Anthropic <-> OpenAI) when a provider is down. Disable to fail requests instead of failing over.",
  },
  "demand-forecasting": {
    type: "boolean",
    defaultValue: false,
    description: "Controls whether demand forecasting is exposed to users.",
  },
  "custom-roles": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether custom roles management (roles settings page) is exposed to users.",
  },
  "person-search-tool": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the paid person search tool is exposed to the standard agent.",
  },
  "salesforce-erp-integration": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the Salesforce ERP integration is exposed to users (e.g. shown in the ERP provider dropdown).",
  },
  "epicor-primitive-support": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether Epicor primitive inputs are exposed to users and the standard agent.",
  },
  "prophet21-support": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the Epicor Prophet 21 ERP integration and its connection setup are exposed to users.",
  },
  "jobboss2-erp-integration": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the JobBOSS² ERP integration is exposed to users (e.g. shown in the ERP provider dropdown).",
  },
  "odyssey-erp-integration": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the Odyssey ERP integration is exposed to users (e.g. shown in the ERP provider dropdown).",
  },
  "eniteo-support": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the Eniteo ERP integration is exposed to users (e.g. shown in the ERP provider dropdown).",
  },
  "enable-tool-search": {
    type: "boolean",
    defaultValue: false,
    description:
      "Enables tool search for agents, allowing on-demand tool discovery instead of loading all tool schemas upfront.",
  },
  "offload-large-tool-results": {
    type: "boolean",
    defaultValue: false,
    description:
      "Writes tool results over the size threshold to a sandbox context file and shows the model a stub.",
  },
  "email-search": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether email search is available in universal search and the Outlook integration UI is visible.",
  },
  "send-message-to-thread": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether Agent SDK threads can route messages into existing non-private Agent SDK threads.",
  },
  "item-comms-messages": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the agent records the comms messages behind an item's fields: the referenceCommsMessageIds input on item create/update. When off, the field is absent from the tool schema.",
  },
  "item-history": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the item editor offers the History tab, which shows an item's recorded change log.",
  },
  "estimation-station": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the Estimation Station (item editor V2) experience is enabled for an org.",
  },
  "image-indexing": {
    type: "boolean",
    defaultValue: true,
    description:
      "Controls whether PDF pages and images are rasterized, stored, and indexed as image embeddings for image-based search. When off, files are still indexed by text and metadata, and scanned PDFs still get OCR text.",
  },
  "ocr-indexing": {
    type: "boolean",
    defaultValue: true,
    description:
      "Controls whether OCR runs during indexing. When off, no Textract OCR or LLM OCR-cleanup pass runs, so image chunks carry no OCR text. Files are still indexed by filename/metadata and by any embedded PDF text layer.",
  },
  "extend-ui-viewer": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether files open in the Extend UI viewers. When off, PDFs open in the browser's native viewer and office files show a download link.",
  },
  "quote-documents": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the Quote Templates V2 HTML-document editor and per-quote documents are exposed to users.",
  },
  "server-side-quote-pdf": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the public quote Export PDF button uses server-side PDF generation instead of browser printing.",
  },
  "status-reports-cutover": {
    type: "boolean",
    defaultValue: false,
    description:
      "When enabled for an org: the Report Settings surface appears (for settings-edit holders), daily status reports send, and the legacy task-reminder cron stops for that org.",
  },
  "multi-org-membership": {
    type: "boolean",
    defaultValue: true,
    description:
      "Rollout flag: when enabled, auth and user reads use organization membership rows as the source of truth. When disabled, the legacy users-table organization/role/status columns are used. Writes always dual-write both stores.",
  },
  "task-inbox-table-view": {
    type: "boolean",
    defaultValue: false,
    description:
      "Renders the task inbox as a filterable table instead of grouped cards.",
  },
  "quote-line-margin": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether the read-only Margin column is shown on quote lines in the quote editor.",
  },
  "vault-synced-folders": {
    type: "boolean",
    defaultValue: false,
    description:
      "Controls whether users can link OneDrive and SharePoint folders to Vault and have them synced.",
  },
} as const satisfies Record<string, FeatureFlagDefinition>;

/** Union of every defined feature flag key. */
export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/** The runtime value type returned for a given flag key. */
export type FeatureFlagValue<K extends FeatureFlagKey> = FeatureFlagValueByType<
  (typeof FEATURE_FLAGS)[K]["type"]
>;

/** Map of every flag key to its evaluated value. */
export type FeatureFlagValues = {
  [K in FeatureFlagKey]: FeatureFlagValue<K>;
};

/** Context passed to the feature flag service when evaluating a flag. */
export interface FeatureFlagContext {
  userId?: string;
  orgId?: string;
}

/** Type-broadened view of the registry for runtime iteration and indexing. */
const FEATURE_FLAG_REGISTRY: Record<string, FeatureFlagDefinition> =
  FEATURE_FLAGS;

/**
 * Returns the code-defined default value for the given flag key.
 * @param key - The flag key to look up.
 * @returns The default value declared in the registry.
 */
export function getFeatureFlagDefault<K extends FeatureFlagKey>(
  key: K,
): FeatureFlagValue<K> {
  return FEATURE_FLAG_REGISTRY[key].defaultValue as FeatureFlagValue<K>;
}

/**
 * Returns a map of every flag key to its code-defined default value.
 * Used as the safe fallback when PostHog is unreachable.
 * @returns Map of flag key to default value.
 */
export function getAllFeatureFlagDefaults(): FeatureFlagValues {
  const result: Record<string, unknown> = {};
  for (const [key, def] of Object.entries(FEATURE_FLAG_REGISTRY)) {
    result[key] = def.defaultValue;
  }
  return result as FeatureFlagValues;
}

/**
 * Zod schema for the evaluated feature flags map. Add an entry here in
 * lockstep with each new {@link FEATURE_FLAGS} entry — the
 * `z.ZodType<FeatureFlagValues>` annotation forces TypeScript to fail the
 * compile if the schema and registry drift apart.
 */
export const FeatureFlagValuesSchema: z.ZodType<FeatureFlagValues> = z.object({
  "quote-review-editable-handoff": z.boolean(),
  "xlsx-validation-agent-feedback": z.boolean(),
  "hubspot-erp-integration": z.boolean(),
  USE_GPT: z.boolean(),
  USE_GPT_SOL_6_1: z.boolean(),
  USE_GPT_LUNA_6: z.boolean(),
  USE_CLAUDE_OPUS_4_8: z.boolean(),
  USE_CLAUDE_OPUS_FAST_MODE: z.boolean(),
  "llm-provider-failover": z.boolean(),
  "demand-forecasting": z.boolean(),
  "custom-roles": z.boolean(),
  "person-search-tool": z.boolean(),
  "salesforce-erp-integration": z.boolean(),
  "epicor-primitive-support": z.boolean(),
  "prophet21-support": z.boolean(),
  "jobboss2-erp-integration": z.boolean(),
  "odyssey-erp-integration": z.boolean(),
  "eniteo-support": z.boolean(),
  "enable-tool-search": z.boolean(),
  "offload-large-tool-results": z.boolean(),
  "email-search": z.boolean(),
  "send-message-to-thread": z.boolean(),
  "item-comms-messages": z.boolean(),
  "item-history": z.boolean(),
  "estimation-station": z.boolean(),
  "image-indexing": z.boolean(),
  "ocr-indexing": z.boolean(),
  "extend-ui-viewer": z.boolean(),
  "quote-documents": z.boolean(),
  "server-side-quote-pdf": z.boolean(),
  "status-reports-cutover": z.boolean(),
  "multi-org-membership": z.boolean(),
  "task-inbox-table-view": z.boolean(),
  "quote-line-margin": z.boolean(),
  "vault-synced-folders": z.boolean(),
});

/** Zod schema for the GET /feature-flags response payload. */
export const FeatureFlagsResponseSchema = z.object({
  flags: FeatureFlagValuesSchema,
});
